import api from '@/core/api/api';

/**
 * Exact literal Stripe substitutes in `success_url` (hosted Checkout).
 * Never replace this with runtime interpolation — Stripe performs substitution after redirect.
 *
 * @see https://docs.stripe.com/payments/checkout/custom-success-page
 */
export const STRIPE_CHECKOUT_SUCCESS_SESSION_TEMPLATE = '{CHECKOUT_SESSION_ID}';

/**
 * Build Stripe hosted Checkout redirect URLs after payment / cancel on the manager dashboard.
 *
 * @param {string} returnBaseAbsoluteURL - Absolute URL without trailing slash (e.g. origin + ROUTES.MANAGER_DASHBOARD).
 * @returns {{ success_url: string, cancel_url: string }}
 */
export function buildManagerHostedCheckoutUrls(returnBaseAbsoluteURL) {
  const base = String(returnBaseAbsoluteURL || '').replace(/\/+$/, '');
  return {
    success_url: `${base}?billing_return=success&scs=${STRIPE_CHECKOUT_SUCCESS_SESSION_TEMPLATE}`,
    cancel_url: `${base}?billing_return=cancel`,
  };
}

/**
 * Billing Service — revenue share / deferred selection and Stripe Checkout subscriptions.
 */

const billingService = {
  /**
   * Select billing model for firm (rev-share or deferred)
   * @param {Object} data - Billing model selection data
   * @param {string} data.firm_id - Firm ID
   * @param {string} data.user_id - User ID
   * @param {string} data.billing_model - 'rev_share' | 'deferred'
   * @param {Object} data.signature_data - Signature details (if rev_share)
   * @returns {Promise} Response with billing model confirmation
   */
  selectBillingModel: async (data) => {
    try {
      if (data.billing_model && !['rev_share', 'deferred'].includes(data.billing_model)) {
        throw new Error('Invalid participation model. Only rev_share and deferred are supported.');
      }

      const response = await api.post('/auth/subscription/select-billing-model', data);
      return response.data;
    } catch (error) {
      console.error('Error selecting billing model:', error);
      throw error;
    }
  },

  /**
   * Start Stripe Checkout for a manager subscription (requires JWT).
   * Backend validates success_url / cancel_url origins against CHECKOUT_ALLOWED_ORIGINS.
   * Hosted Checkout: include `{CHECKOUT_SESSION_ID}` exactly as in Stripe docs via
   * `buildManagerHostedCheckoutUrls()`.
   * Only annual subscriptions are supported; plan defaults to 'annual'.
   */
  createCheckoutSession: async ({ plan = 'annual', success_url, cancel_url }) => {
    const response = await api.post('/auth/billing/create-checkout-session', {
      plan,
      success_url,
      cancel_url
    });
    return response.data;
  },

  /**
   * Record e-signed subscription agreement before Stripe Checkout.
   * @param {{ firm_id: string, user_id: string, signature_data: object }} data
   */
  recordSubscriptionAgreement: async (data) => {
    const response = await api.post('/auth/subscription/record-subscription-agreement', data);
    return response.data;
  },

  /**
   * Stripe Customer Portal session (requires firm_stripe_customer_id).
   */
  createCustomerPortalSession: async ({ return_url }) => {
    const response = await api.post('/auth/billing/customer-portal', {
      return_url
    });
    return response.data;
  },

  /**
   * Pull real-time subscription data from Stripe and write it back to DynamoDB.
   * Call this before refreshUserData() when returning from the Stripe Portal
   * or when the manager manually refreshes, so the local snapshot is current.
   *
   * @param {Object} [options]
   * @param {string} [options.checkout_session_id] - Stripe Checkout Session ID; passed when
   *   returning from Checkout before the webhook has landed (deferred → subscription flow).
   */
  syncSubscription: async (options = {}) => {
    const response = await api.post('/auth/billing/sync-subscription', options);
    return response.data;
  },
};

export default billingService;
